import { useEffect, useRef } from 'react';
import { useStore } from '../../store';
import { FeatureFlag } from '../../constants/featureFlags';
import { useInbox, useUnreadTotal } from '../../hooks/chat/useChatQueries';
import { useChatEvents } from '../../hooks/chat/useChatEvents';
import { useMaxOpenWindows } from '../../hooks/chat/useMaxOpenWindows';
import { useChatKeyboard } from '../../hooks/chat/useChatKeyboard';
import { useChatWindowPersistence } from '../../hooks/chat/useChatWindowPersistence';
import { useUnreadTitle } from '../../hooks/chat/useUnreadTitle';
import { useMediaQuery } from '../../hooks/useIsMobile';
import { ChatDock } from './ChatDock';
import { ChatSheet } from './ChatSheet';
import { ChatTab } from './ChatTab';
import { ChatWindow } from './ChatWindow';

/**
 * The one chat mount point, in Layout so the windows survive route changes.
 * Gated by the WorkOS `chat` flag alone: there is no access slug.
 *
 * The gate is a separate component from the hooks on purpose: every hook below
 * uses TanStack Query, and Layout.test.tsx renders `<Layout />` with no
 * QueryClientProvider. Running one of them for a flag-off user would throw
 * there, and would also fire a 403 GET /chat/inbox for that user.
 */
export function ChatTray() {
  const enabled = useStore(state =>
    state.hasFeatureFlag(FeatureFlag.CHAT, 'workos')
  );
  if (!enabled) return null;
  return <ChatTrayInner />;
}

function ChatTrayInner() {
  const open = useStore(state => state.chatWindows.open);
  const minimized = useStore(state => state.chatWindows.minimized);
  const setChatLayout = useStore(state => state.setChatLayout);
  const setChatSheetOpen = useStore(state => state.setChatSheetOpen);
  const setChatSheetView = useStore(state => state.setChatSheetView);
  const setChatDockOpen = useStore(state => state.setChatDockOpen);
  const minimizeChatWindow = useStore(state => state.minimizeChatWindow);
  useInbox();
  const unread = useUnreadTotal();
  useChatEvents();
  useUnreadTitle(unread);
  useMaxOpenWindows();
  useChatKeyboard();
  useChatWindowPersistence();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const wasDesktop = useRef(isDesktop);
  useEffect(() => {
    const { chatWindows, chatSheetOpen, chatSheetView } = useStore.getState();
    if (wasDesktop.current && !isDesktop) {
      // The viewport shrank below 1024 px: the sheet takes over with the
      // newest conversation, the inbox when only the dock was open, or stays
      // closed. The flag is set in every branch so it never goes stale.
      if (chatWindows.open.length > 0) {
        setChatSheetOpen(true);
        setChatSheetView('conversation');
      } else {
        setChatSheetOpen(chatWindows.dockOpen);
        setChatSheetView('inbox');
      }
    } else if (!wasDesktop.current && isDesktop) {
      // The viewport grew back: what the sheet showed carries over. A closed
      // sheet, or an inbox, leaves no window on screen, so open windows become
      // tabs (they must not be marked read unseen).
      if (!chatSheetOpen || chatSheetView === 'inbox') {
        for (const id of chatWindows.open) minimizeChatWindow(id);
        setChatDockOpen(chatSheetOpen);
      }
      setChatSheetOpen(false);
      setChatSheetView('inbox');
    }
    setChatLayout(isDesktop ? 'tray' : 'sheet');
    wasDesktop.current = isDesktop;
  }, [
    isDesktop,
    setChatLayout,
    setChatSheetOpen,
    setChatSheetView,
    setChatDockOpen,
    minimizeChatWindow,
  ]);

  // Below 1024 px one bottom sheet replaces the tray.
  if (!isDesktop) return <ChatSheet />;

  return (
    <div
      role='region'
      aria-label='Chat'
      className='pointer-events-none fixed bottom-0 right-6 z-40 flex flex-row-reverse items-end gap-3'
    >
      <ChatDock />
      {open.map(id => (
        <ChatWindow key={id} id={id} />
      ))}
      {minimized.length > 0 && (
        <div className='-m-1 flex max-h-[calc(100dvh-80px)] flex-col gap-2 overflow-y-auto p-1'>
          {minimized.map(id => (
            <ChatTab key={id} id={id} />
          ))}
        </div>
      )}
    </div>
  );
}
